/**
 * EG Search (Meilisearch) – /search page controller.
 *
 * Orchestrates the /search page using:
 * - `EGSearchResultsApi` (fetch + query params)
 * - `EGSearchResultsRender` (DOM rendering)
 * - `EGSearchResultsInfinite` (mobile infinite scroll)
 */
window.EGSearchResultsPage = (function () {
  'use strict';

  const api = window.EGSearchResultsApi;
  const render = window.EGSearchResultsRender;
  const infinite = window.EGSearchResultsInfinite;

  // State
  let currentPage = 0;
  let currentQuery = '';
  let currentFilters = {};
  let currentSort = '';
  let currentSearchId = '';
  let searchGeneration = 0;

  // Mobile helpers
  function isMobileFilters() {
    return window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
  }

  function isMobileInfiniteScroll() {
    return window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
  }

  function getSearchSectionRoot() {
    return document.querySelector('.section-meili-search') || document.querySelector('#MainContent');
  }

  function setMobileFacetsOpen(open) {
    const root = getSearchSectionRoot();
    const btn = document.querySelector('.meili-search__filters-toggle');
    if (root) root.classList.toggle('meili-facets-open', !!open);
    if (btn) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.textContent = open ? 'Hide filters' : 'Show filters';
    }
  }

  // State helpers
  function getCurrentQuery() { return currentQuery; }
  function getCurrentSearchId() { return currentSearchId; }
  function setCurrentQuery(q) { currentQuery = q; }
  function setCurrentPage(p) { currentPage = p; }

  // URL filters (Shopify -> Meili)
  /**
   * Parse Shopify's filter query params and convert them into our Meilisearch filter shape.
   * Examples:
   * - `filter.v.price.gte=10` -> `{ _priceRange: { gte: 10 } }`
   * - `filter.p.product_type=Seeds` -> `{ product_type: ['Seeds'] }`
   */
  function parseShopifyFilters() {
    const urlParams = new URLSearchParams(window.location.search);
    const filters = {};
    const priceFilter = {};

    // Only include filters we actually support in the Meilisearch index. This avoids
    // "attribute is not filterable" errors when Shopify exposes extra filters.
    const ALLOWED_FIELDS = new Set([
      'product_type',
      'price',
      'compare_at_price',
      'inventory_available',
      'variants_count',
      'requires_shipping',
      'popularity_score',
      'order_count',
    ]);

    for (const [key, value] of urlParams.entries()) {
      if (!key.startsWith('filter.')) continue;

      // Example keys: `filter.v.price.gte`, `filter.p.product_type`, etc.
      const parts = key.split('.');
      if (parts.length < 3) continue;

      const filterName = parts[2]; // price, product_type, availability, etc.
      const filterOp = parts[3]; // gte/lte (for price ranges)

      // Price range filters
      if (filterName === 'price') {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          if (filterOp === 'gte') priceFilter.gte = numValue;
          if (filterOp === 'lte') priceFilter.lte = numValue;
        }
        continue;
      }

      // List-style filters (e.g. product_type)
      // Map Shopify filter names to our indexed field names.
      const decodedValue = decodeURIComponent(value);

      let meiliField = null;
      let meiliValue = decodedValue;

      // Shopify availability filter -> our indexed boolean field.
      // Typical Shopify: `filter.v.availability=1` (in stock) / `0` (out of stock)
      if (filterName === 'availability') {
        meiliField = 'inventory_available';
        const v = String(decodedValue).toLowerCase();
        meiliValue = v === '1' || v === 'true' || v === 'yes' || v === 'available' || v === 'in_stock';
      }
      // Pass-through for fields we index directly
      else if (filterName === 'product_type') {
        meiliField = filterName;
      }
      // Not indexed (options/metafields/other Shopify filters)
      else {
        meiliField = null;
      }

      if (!meiliField || !ALLOWED_FIELDS.has(meiliField)) {
        continue;
      }

      if (!filters[meiliField]) {
        filters[meiliField] = [];
      }

      if (!filters[meiliField].includes(meiliValue)) {
        filters[meiliField].push(meiliValue);
      }
    }

    // Add price range to filters if present.
    if (priceFilter.gte !== undefined || priceFilter.lte !== undefined) {
      filters._priceRange = priceFilter;
    }

    return filters;
  }

  // Header
  function renderSearchHeader() {
    const headerContainer = document.querySelector('.meili-search__header');
    if (!headerContainer) return;

    if (!window.location.pathname.startsWith('/search')) {
      headerContainer.style.display = 'none';
      return;
    }

    headerContainer.style.display = '';

    if (headerContainer.querySelector('.meili-search__title')) return;

    headerContainer.innerHTML = `
      <h1 class="meili-search__title">Search results</h1>
      <div class="meili-search__form">
        <input type="search" id="search-input" name="q"
               value="${window.EGSearchConfig.escapeHtml(currentQuery)}"
               placeholder="Search products..." aria-label="Search products"
               autocomplete="off" />
      </div>
      <nav class="eg-search-scopes" aria-label="Search in"><a data-eg-product-scope aria-current="page" href="/search?q=${encodeURIComponent(currentQuery)}">Products</a><a data-eg-guide-scope href="/?s=${encodeURIComponent(currentQuery)}">Growing guides</a></nav>
      <button type="button" class="meili-search__filters-toggle button button--secondary" aria-expanded="true">
        Show filters
      </button>
    `;

    const input = headerContainer.querySelector('#search-input');
    if (input) {
      let searchTimeout;

      input.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          if (input.value.trim()) {
            currentQuery = input.value.trim();
            currentPage = 0;
            performSearch();
          }
        }, 300);
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && input.value.trim()) {
          e.preventDefault();
          clearTimeout(searchTimeout);
          currentQuery = input.value.trim();
          currentPage = 0;
          performSearch();
        }
      });
    }

    // Mobile: default to collapsed filters, with a toggle button under the search bar.
    const filtersBtn = headerContainer.querySelector('.meili-search__filters-toggle');
    if (filtersBtn) {
      if (isMobileFilters()) setMobileFacetsOpen(false);

      filtersBtn.addEventListener('click', () => {
        const root = getSearchSectionRoot();
        const isOpen = !!root?.classList?.contains('meili-facets-open');
        setMobileFacetsOpen(!isOpen);
      });

      window.addEventListener('resize', () => {
        if (!isMobileFilters()) {
          setMobileFacetsOpen(true);
        }
      });
    }
  }

  // Search
  async function performSearch() {
    const generation = ++searchGeneration;
    renderSearchHeader();
    document.querySelector('[data-eg-product-scope]')?.setAttribute('href', '/search?q=' + encodeURIComponent(currentQuery));
    document.querySelector('[data-eg-guide-scope]')?.setAttribute('href', '/?s=' + encodeURIComponent(currentQuery));
    document.title = 'Search: ' + currentQuery + ' - Epic Gardening';

    const resultsContainer = document.getElementById('search-results') ||
      document.getElementById('algoliaResult') ||
      document.querySelector('.ais-Hits-list');
    const statsContainer = document.getElementById('search-stats') ||
      document.querySelector('.ais-Stats');
    const facetsContainer = document.getElementById('search-facets') ||
      document.querySelector('.ais-facets');

    let paginationContainer = document.getElementById('search-pagination') ||
      document.querySelector('.ais-Pagination');

    if (!resultsContainer) return;

    // Ensure the pagination container exists.
    if (!paginationContainer) {
      paginationContainer = document.createElement('div');
      paginationContainer.id = 'search-pagination';
      paginationContainer.className = 'ais-Pagination';
      resultsContainer.parentNode.insertBefore(paginationContainer, resultsContainer.nextSibling);
    }

    // Reset infinite scroll for new searches.
    infinite?.teardown?.();
    window.EGSearchVariants?.resetScheduledCards();

    try {
      resultsContainer.innerHTML = '<div class="loading-spinner"><div class="spinner"></div></div>';

      // Query string sent to the API.
      const effectiveQuery = currentQuery || '';
      const data = await api.fetchSearchPage({
        query: effectiveQuery,
        page: currentPage,
        hitsPerPage: api.HITS_PER_PAGE,
        facets: api.FACETS_LIST,
        filters: currentFilters,
        sort: currentSort,
        onHits: hits => {
          if (generation === searchGeneration) render.preloadFirstImage(hits);
        },
      });
      if (generation !== searchGeneration) return;
      currentSearchId = String(data.search_id || '');

      const uniqueHits = render.dedupeHitsByHandle(data.hits || []);

      render.renderStats(data, statsContainer, uniqueHits.length, currentSort, isMobileInfiniteScroll());
      const liveRegion = document.getElementById('SearchResultsLiveRegion');
      if (liveRegion) {
        const total = Number(data.nbHits) || 0;
        liveRegion.textContent = `${total} ${total === 1 ? 'product' : 'products'} found.`;
      }

      // Active filters chips
      let activeFiltersContainer = document.getElementById('search-active-filters');
      if (!activeFiltersContainer && statsContainer) {
        activeFiltersContainer = document.createElement('div');
        activeFiltersContainer.id = 'search-active-filters';
        statsContainer.parentNode.insertBefore(activeFiltersContainer, statsContainer);
      }
      render.renderActiveFilters(currentFilters, activeFiltersContainer);

      render.renderHits(uniqueHits, resultsContainer, currentQuery, currentSearchId);
      render.renderFacets(data.facets || {}, facetsContainer, currentFilters, !isMobileFilters(), currentQuery);

      if (isMobileInfiniteScroll()) {
        if (paginationContainer) paginationContainer.innerHTML = '';
        infinite?.setup?.({
          resultsContainer,
          statsContainer,
          data,
          initialHits: uniqueHits,
          fetchPage: (page) => api.fetchSearchPage({
            query: currentQuery || '',
            page,
            hitsPerPage: api.HITS_PER_PAGE,
            facets: api.FACETS_LIST,
            filters: currentFilters,
            sort: currentSort,
          }),
          dedupe: render.dedupeHitsByHandle,
          appendHits: (hits, container, startIndex) => {
            const list = container.querySelector('.ais-Hits-list');
            if (!list) return;
            list.insertAdjacentHTML(
              'beforeend',
              hits.map((hit, idx) => render.renderProductCardFull(hit, startIndex + idx, currentQuery, currentSearchId)).join('')
            );
            window.EGSearchVariants?.scheduleCards(hits, container, startIndex);
            render.refreshIntelligemsPrices?.();
          },
          updateStats: (dataForStats, containerForStats, displayedCount) => {
            render.renderStats(dataForStats, containerForStats, displayedCount, currentSort, true);
          },
        });
      } else {
        render.renderPagination(data, paginationContainer);
      }
    } catch (err) {
      if (generation !== searchGeneration) return;
      console.error('Search error:', err);
      resultsContainer.innerHTML = '<p class="no-results">Error loading products. Please try again.</p>';
    }
  }

  // Global helpers used by inline handlers (onclick / onchange).
  window.customSearchToggleFilter = function (facet, value) {
    // console.log('[MeiliDebug] Toggle Filter:', facet, value, typeof value);
    if (!currentFilters[facet]) currentFilters[facet] = [];

    const index = currentFilters[facet].indexOf(value);
    if (index > -1) {
      // console.log('[MeiliDebug] Removing value at index', index);
      currentFilters[facet].splice(index, 1);
    } else {
      // console.log('[MeiliDebug] Adding value');
      currentFilters[facet].push(value);
    }
    // console.log('[MeiliDebug] New State:', JSON.parse(JSON.stringify(currentFilters)));

    if (Array.isArray(currentFilters[facet]) && currentFilters[facet].length === 0) {
      delete currentFilters[facet];
    }

    currentPage = 0;
    performSearch();
  };

  window.customSearchClearAll = function () {
    currentFilters = {};
    currentPage = 0;
    performSearch();
  };

  window.customSearchSetPriceRange = function (min, max) {
    const range = {};
    const minNum = min !== null && min !== undefined && String(min).trim() !== '' ? Number(min) : NaN;
    const maxNum = max !== null && max !== undefined && String(max).trim() !== '' ? Number(max) : NaN;
    if (Number.isFinite(minNum)) range.gte = minNum;
    if (Number.isFinite(maxNum)) range.lte = maxNum;

    if (range.gte === undefined && range.lte === undefined) {
      delete currentFilters._priceRange;
    } else {
      currentFilters._priceRange = range;
    }

    currentPage = 0;
    performSearch();
  };

  window.customSearchGoToPage = function (page) {
    currentPage = page;
    performSearch();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  window.customSearchSetSort = function (sort) {
    currentSort = sort;
    currentPage = 0;
    performSearch();
  };

  // Init
  function init() {
    const urlParams = new URLSearchParams(window.location.search);
    currentQuery = urlParams.get('q') || urlParams.get('query') || '';

    // Parse Shopify filter params from the URL.
    currentFilters = parseShopifyFilters();
    // console.log('Parsed Shopify filters:', currentFilters);

    // Our facets script updates the URL via pushState; when that happens, re-run the search.
    window.addEventListener('eg:facets:updated', () => {
      const nextParams = new URLSearchParams(window.location.search);
      currentQuery = nextParams.get('q') || nextParams.get('query') || currentQuery || '';
      currentFilters = parseShopifyFilters();
      currentPage = 0;
      performSearch();
    });

    // Back/forward navigation should also re-run the search.
    window.addEventListener('popstate', () => {
      const nextParams = new URLSearchParams(window.location.search);
      currentQuery = nextParams.get('q') || nextParams.get('query') || '';
      currentFilters = parseShopifyFilters();
      currentPage = 0;
      performSearch();
    });

    const searchInput = document.getElementById('autocomplete-0-input') ||
      document.getElementById('search-input');
    if (searchInput && !currentQuery) {
      currentQuery = searchInput.value.trim();
    }

    const isSearchPage = window.location.pathname.startsWith('/search');
    if (isSearchPage && (document.getElementById('search-results') ||
      document.getElementById('algoliaResult') ||
      document.querySelector('.ais-Hits-list'))) {
      // Always perform search on search page (even without query, filters may be set)
      performSearch();
    }

    if (searchInput) {
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && searchInput.value.trim()) {
          e.preventDefault();
          currentQuery = searchInput.value.trim();
          currentPage = 0;
          performSearch();
        }
      });
    }
  }

  return {
    init,
    performSearch,
    getCurrentQuery,
    getCurrentSearchId,
    setCurrentQuery,
    setCurrentPage,
  };
})();
